import { CommonModule } from '@angular/common';
import { Component, ElementRef, inject } from '@angular/core';
import { PushPipe } from '@ngrx/component';
import { WdxCopyToClipboardComponent } from '@wdx/shared/components/wdx-copy-to-clipboard';
import {
    CurrencyValue,
    FormFieldType,
    IMeLocale,
    InvestmentDataColumnConfiguration,
    InvestmentDataSectionConfiguration,
    IsNilPipe,
    Locale,
    TranslateTokenisedStringPipe,
    ViewColumnType,
    WdxDateFormat,
    WdxDateTimePipe,
    WdxNumberService,
} from '@wdx/shared/utils';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams, IRowNode } from 'ag-grid-community';
import { map, Observable } from 'rxjs';
import { Transaction } from '../../models';

export type PortfolioDetailRendererParams = ICellRendererParams<Transaction> & {
    locale?: Locale | null;
    getConfig$: () => Observable<{
        sections?: InvestmentDataSectionConfiguration[] | null;
        fields?: InvestmentDataColumnConfiguration[];
    }>;
    updateDetailRowHeight: (rowNode: IRowNode, height: number) => void;
};

@Component({
    templateUrl: './portfolio-detail-renderer.component.html',
    styleUrls: ['./portfolio-detail-renderer.component.scss'],
    imports: [
        CommonModule,
        IsNilPipe,
        TranslateTokenisedStringPipe,
        WdxCopyToClipboardComponent,
        WdxDateTimePipe,
        PushPipe,
    ],
    host: {
        class: 'w-100 grid px-2 px-md-5 py-1 py-md-3 bg-secondary-light bg-opacity-25',
    },
})
export class PortfolioDetailCellRendererComponent implements ICellRendererAngularComp {
    private numberService = inject(WdxNumberService);
    private localeService = inject(IMeLocale);

    public data?: any;
    public readonly FormFieldType = FormFieldType;
    public readonly WdxDateFormat = WdxDateFormat;
    public readonly ViewColumnType = ViewColumnType;
    public locale = this.localeService.getMeLocale();
    public currencyCode?: string | null | undefined;
    public config$?: Observable<{
        sectionFields?: {
            sectionName: string;
            fields: InvestmentDataColumnConfiguration[];
        }[];
        noSectionFields?: InvestmentDataColumnConfiguration[];
    }>;

    private params?: PortfolioDetailRendererParams;
    private observer = new ResizeObserver(([{ borderBoxSize }]) => {
        if (borderBoxSize[0]?.blockSize > 0) {
            this.params?.updateDetailRowHeight(
                this.params.node,
                borderBoxSize[0]?.blockSize,
            );
        }
    });

    private elementRef = inject(ElementRef);

    public agInit(params: PortfolioDetailRendererParams): void {
        this.params = params;
        this.observer.observe(this.elementRef.nativeElement);
        this.config$ = params.getConfig$().pipe(
            map((config) => {
                if (!config?.sections?.length) {
                    return { noSectionFields: config.fields };
                }
                const noSectionFields: InvestmentDataColumnConfiguration[] = [];
                const sectionFields: {
                    sectionName: string;
                    fields: InvestmentDataColumnConfiguration[];
                }[] = [];
                config.fields?.forEach((field) => {
                    const section = config.sections?.find(
                        (section) => section.code === field.sectionCode,
                    );
                    if (!section?.name) {
                        noSectionFields.push(field);
                        return;
                    }
                    const existingSection = sectionFields.find(
                        (sf) => sf.sectionName === section?.name,
                    );
                    if (existingSection) {
                        existingSection.fields.push(field);
                        return;
                    }
                    sectionFields.push({
                        sectionName: section.name,
                        fields: [field],
                    });
                });
                config.sections.map((section) => section.name);
                return { noSectionFields, sectionFields };
            }),
        );
        this.data = params.data as any;
        this.currencyCode = params.data?.currency?.isoCode;
    }

    refresh(params: PortfolioDetailRendererParams): boolean {
        this.agInit(params);
        return false;
    }

    formatCurrency(value: CurrencyValue): string | undefined {
        const currencyCode = value?.isoCode ?? this.currencyCode ?? '';
        if (value?.value === undefined) {
            return undefined;
        }
        if (this.locale === undefined) {
            return (
                this.numberService.formatLocalisedCurrency(
                    value.value as number,
                    {
                        currencyCode,
                    },
                ) ?? undefined
            );
        }
        return (
            this.numberService.formatLocalisedCurrency(value.value as number, {
                currencyCode,
                locale: this.locale?.code as string,
                groupSeparator: this.locale?.numberFormat
                    ?.numberGroupSeparator as string,
                decimalSeparator: this.locale?.numberFormat
                    ?.numberDecimalSeparator as string,
            }) ?? undefined
        );
    }
}
